import React from 'react';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { profesionStudies } from '../../../../../types/Profesionals/professional';
import styles from './ProfessionalStudiesTab.module.scss';

interface StudyEditorCardProps {
    study: profesionStudies;
    index: number;          // visual number (1-based among non-deleted)
    isFirst: boolean;
    isLast: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onViewCertificate?: (url: string, name: string) => void;
}

export const StudyEditorCard: React.FC<StudyEditorCardProps> = ({
    study,
    index,
    isFirst,
    isLast,
    onMoveUp,
    onMoveDown,
    onDelete,
    onEdit,
    onViewCertificate,
}) => {
    const { t } = useTranslation();

    const formatDate = (d?: string) => {
        if (!d) return null;
        return dayjs(d).isValid() ? dayjs(d).format('DD/MM/YYYY') : null;
    };

    const end = formatDate(study.date_end);

    let dateRange: string | null = null;
    if (end) dateRange = end;

    const hasCert = !!study.url_certificate;
    const typeLabel = study.type_other || study.type?.label || study.type?.code;

    const handleCertClick = () => {
        if (!study.url_certificate || !onViewCertificate) return;
        const url = typeof study.url_certificate === 'string'
            ? `${process.env.REACT_APP_API_URL}${study.url_certificate}`
            : URL.createObjectURL(study.url_certificate as File);
        onViewCertificate(url, study.name || '');
    };

    return (
        <article className={styles['study-editor-card']}>
            {/* Head row */}
            <div className={styles['study-editor-card__head']}>
                <span className={styles['study-editor-card__number']}>
                    {t('appAdmin.EditTabsComponent.studyNumber', 'Estudio')} {index}
                </span>
                <div className={styles['study-editor-card__actions']}>
                    <button
                        className={styles['study-editor-card__icon-btn']}
                        type="button"
                        title={t('common.moveUp', 'Subir')}
                        disabled={isFirst}
                        onClick={onMoveUp}
                    >
                        <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />
                    </button>
                    <button
                        className={styles['study-editor-card__icon-btn']}
                        type="button"
                        title={t('common.moveDown', 'Bajar')}
                        disabled={isLast}
                        onClick={onMoveDown}
                    >
                        <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />
                    </button>
                    <button
                        className={`${styles['study-editor-card__icon-btn']} ${styles['study-editor-card__icon-btn--edit']}`}
                        type="button"
                        title={t('common.edit', 'Editar')}
                        onClick={onEdit}
                    >
                        <EditRoundedIcon sx={{ fontSize: 14 }} />
                    </button>
                    <button
                        className={`${styles['study-editor-card__icon-btn']} ${styles['study-editor-card__icon-btn--delete']}`}
                        type="button"
                        title={t('common.delete', 'Eliminar')}
                        onClick={onDelete}
                    >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className={styles['study-editor-card__body']}>
                <strong className={styles['study-editor-card__title']}>{study.name}</strong>
                {study.institute && (
                    <span className={styles['study-editor-card__institution']}>{study.institute}</span>
                )}

                {typeLabel && (
                    <div className={styles['study-editor-card__tags']}>
                        <span className={styles['study-editor-card__tag']}>{typeLabel}</span>
                    </div>
                )}

                {(dateRange || hasCert) && (
                    <div className={styles['study-editor-card__meta']}>
                        {dateRange && <span>{dateRange}</span>}
                        {dateRange && hasCert && <span> · </span>}
                        {hasCert && (
                            <button
                                type="button"
                                className={styles['study-editor-card__cert-link']}
                                onClick={handleCertClick}
                            >
                                {t('appAdmin.EditTabsComponent.viewCertificate', 'Ver Certificado')}
                            </button>
                        )}
                        {!hasCert && dateRange && null}
                    </div>
                )}
            </div>
        </article>
    );
};
